import { Permission } from '@/types/enums/Permission';
import { defineStore } from 'pinia';
import { useConfig } from '@/store';
import Keycloak from 'keycloak-js';
import { formatUrl } from '@/utils/url';
import { computed, readonly, ref } from 'vue';
import { useViewStore } from "@/store/ViewStore.ts";

type KeycloakInstance = InstanceType<typeof Keycloak>;

interface UserData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  calculatedName: string;
}

export default defineStore('user', () => {
  let keycloak: KeycloakInstance | undefined = undefined;

  const token = ref<string>();
  const user = ref<UserData>({} as UserData);
  const authenticated = ref(false);

  const fullNameParts = computed(() => [user.value.firstName, user.value.lastName].filter(Boolean));

  const initials = computed(() => fullNameParts.value.map(p => p.charAt(0)).join(''));

  const fullName = computed(() => fullNameParts.value.join(' '));

  function scheduleTokenRefresh() {
    const expMs = keycloak!.tokenParsed!.exp! * 1000;

    // refresh 15 sec before expiration
    const refreshAfterMs = new Date(expMs).getTime() - new Date().getTime() - 15 * 1000;

    window.setTimeout(async () => {
      try {
        await keycloak!.updateToken(30);
      } catch {
        await logout();
        return;
      }
      updateTokenData();
      scheduleTokenRefresh();
    }, refreshAfterMs);
  }

  async function silentLogin(skipAuthCheck = false) {
    if (keycloak) {
      return keycloak.authenticated || false;
    }
    
    keycloak = new Keycloak(useConfig().configuration.keycloak);

    try {
      const isAuthenticated = await keycloak.init({
        onLoad: skipAuthCheck ? undefined : 'check-sso',
        silentCheckSsoRedirectUri: skipAuthCheck ? undefined : formatUrl(`${window.location.origin}/silent-check-sso.html`),
        checkLoginIframe: false,
        enableLogging: true,
        redirectUri: window.location.origin + window.location.pathname + window.location.search,
      });
      
      cleanupUrl();

      authenticated.value = isAuthenticated;

      if (isAuthenticated) {
        updateTokenData();
        scheduleTokenRefresh();
      }

      return isAuthenticated;
    } catch (err) {
      console.error('Failed to authenticate', err);
      authenticated.value = false;
      
      cleanupUrl();

      document.getElementById('app')!.innerHTML = `<div class="text-h5 text-center mt-10">Aplikace je nedostupná</div><div class="text-h5 text-center">The application is unavailable</div>`;

      return false;
    }
  }

  function cleanupUrl() {
    const url = new URL(window.location.href);
    const keycloakParams = ['state', 'session_state', 'code', 'error', 'error_description'];

    let hasKeycloakParams = false;
    keycloakParams.forEach(param => {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param);
        hasKeycloakParams = true;
      }
    });

    if (url.hash) {
      const hashParams = new URLSearchParams(url.hash.substring(1));
      keycloakParams.forEach(param => {
        if (hashParams.has(param)) {
          hashParams.delete(param);
          hasKeycloakParams = true;
        }
      });

      const newHash = hashParams.toString();
      url.hash = newHash ? `#${newHash}` : '';
    }

    if (hasKeycloakParams) {
      window.history.replaceState({}, document.title, url.toString());
    }
  }

  async function login() {
    if (!keycloak) {
      await silentLogin();
    }

    if (keycloak && !keycloak.authenticated) {
      await keycloak.login({
        redirectUri: window.location.href,
      });
    }
  }

  function updateTokenData() {
    const viewStore = useViewStore();
    
    if (keycloak?.authenticated) {
      token.value = `Bearer ${keycloak.token!}`;
      user.value = {
        id: keycloak.tokenParsed!.sub,
        firstName: keycloak.tokenParsed!.given_name ?? '',
        lastName: keycloak.tokenParsed!.family_name ?? '',
        email: keycloak.tokenParsed!.preferred_username ?? '',
      } as UserData;

      user.value.calculatedName = `${user.value.firstName} ${user.value.lastName}`.trim();
      authenticated.value = true;
      
      if (hasPermission(Permission.Dealer)) {
        viewStore.setView('dealer');
      } else if (hasPermission(Permission.FleetManager)) {
        viewStore.setView('fleet-manager');
      } else {
        viewStore.setView('internal');
      }
    }
  }
  
  function closeModalInUrl() {
    const url = new URL(window.location.href);
    
    if (url.searchParams.has('modalExternalReference')) {
      ['modalExternalReference', 'modalType', 'tab'].forEach(param => url.searchParams.delete(param));
      window.history.replaceState({}, document.title, url.toString());
    }
    
    return url.toString();
  }
  
  async function logout() {
    const redirectUri = closeModalInUrl();
    
    await keycloak?.logout({ redirectUri });
    authenticated.value = false;
    token.value = undefined;
    user.value = {} as UserData;
  }

  function isAuthenticated() {
    return authenticated.value;
  }

  function hasPermission(...permissions: Permission[]) {
    return permissions.length === 0
      ? true
      : isAdmin() || permissions.some(r => keycloak?.hasResourceRole(r.toString()));
  }
  
  function getUserRoles(): Permission[] {
    if (!keycloak?.authenticated) {
      return [];
    }

    const userRoles: Permission[] = [];

    // Permission mixes roles and claims; only the 'role.' prefixed members are roles
    Object.values(Permission)
      .filter(permission => permission.toString().startsWith('role.'))
      .forEach(permission => {
        if (keycloak?.hasResourceRole(permission.toString())) {
          userRoles.push(permission);
        }
      });
    
    return userRoles;
  }

  function isAdmin() {
    return keycloak?.hasResourceRole(Permission.Administrator.toString());
  }

  return {
    isAuthenticated,
    silentLogin,
    login,
    logout,
    hasPermission,
    getUserRoles,
    user: readonly(user),
    fullName,
    initials,
    token: readonly(token),
    authenticated: readonly(authenticated),
    get keycloak() { return keycloak; },
  };
});
